import { ReactIcons } from "@kalyzee/kalyzee-ui";
import { useAuthContext } from "@kalyzee/kast-web-sdk";
import { Login } from "@kalyzee/kast-web-sdk/adapted-web-components";
import { AUTH_TYPE } from "@kalyzee/kast-web-sdk/http";
import { AuthenticationMethod, AuthenticationResult } from "@kalyzee/kast-web-sdk/web-components";
import { useEffect, useMemo, useState } from "react";
import { useAppStoreSelector } from "../../../contexts/appStore/selectors";
import { useSdkContext } from "../../../contexts/sdk/context";
import { toastError } from "../../../helpers/toast";
import styles from "./auth.module.css";
import { getGlobalQuery, NavigateGlobalQuery } from "../../../helpers/navigation";

const AUTH_TYPE_TO_METHOD: Partial<Record<AUTH_TYPE, AuthenticationMethod>> = {
  [AUTH_TYPE.LOCAL]: AuthenticationMethod.Local,
  [AUTH_TYPE.GOOGLE]: AuthenticationMethod.Google,
  [AUTH_TYPE.AZURE_AD]: AuthenticationMethod.Microsoft,
};

/**
 * The central authentication block of the login page (regular SDK <Login>).
 * Guest access is not a login-page concern anymore: guests join a meeting
 * from its public lobby.
 */
export const AuthBlock = () => {
  const { setTokens } = useAuthContext();
  const sdkCtx = useSdkContext();
  const orgInfo = useAppStoreSelector((state) => state.orgInfo);

  const [oAuthError, setOAuthError] = useState<boolean>();

  const enabledMethods = useMemo(() => {
    if (!orgInfo) return undefined;
    const map = orgInfo.authMethods;
    const result: AuthenticationMethod[] = [];
    // Entries rather than keys + lookup: the payload only carries the methods
    // the backend knows about, so it cannot be indexed by the whole AUTH_TYPE.
    for (const [type, enabled] of Object.entries(map) as [AUTH_TYPE, unknown][]) {
      if (!enabled) continue;
      const mapped = AUTH_TYPE_TO_METHOD[type];
      if (mapped && !result.includes(mapped)) result.push(mapped);
    }
    return result;
  }, [orgInfo]);

  useEffect(() => {
    const error = getGlobalQuery(window.location.href, NavigateGlobalQuery.BACKEND_ERROR);
    if (error) {
      setOAuthError(true);
      console.error("Error while authenticating with Google: ", error);
    }
  }, []);

  const onResult = async (result: AuthenticationResult) => {
    if (result.success) {
      const tokens = result.tokens;
      if (tokens?.token) {
        setTokens({ token: tokens.token, refreshToken: tokens.refreshToken });
      }
    } else {
      toastError("Identifiants incorrectes");
    }
  };

  return (
    <div className={styles.block}>
      {!orgInfo ? (
        <div className={styles.loading}>
          <ReactIcons.Spinner style={{ width: "2em", height: "2em" }} />
        </div>
      ) : (
        <Login
          className={styles.login}
          sdk={sdkCtx.client}
          logo={null}
          methods={enabledMethods ?? []}
          oAuthRedirectUri={window.origin}
          onResult={onResult}
          googleAuthentificationProps={oAuthError ? { error: "Une erreur est survenue" } : undefined}
          localAuthentificationProps={{
            placeholderEmail: "Email",
            placeholderPassword: "Mot de passe",
          }}
        />
      )}
    </div>
  );
};

export default AuthBlock;
